<template>
  <v-container fluid>
    <v-card>
      <v-card-title>КПИ РГ</v-card-title>

      <v-card-text>
        <v-form ref="form" lazy-validation>
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-autocomplete
                v-model="filters.groupIds"
                :items="groups"
                :loading="dictionariesLoading"
                :rules="[rules.selectionRequired]"
                item-value="id"
                item-text="name"
                label="Группы"
                no-data-text="Ничего не найдено"
                multiple
                chips
                small-chips
                deletable-chips
                clearable
              />
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-autocomplete
                v-model="filters.rgIds"
                :items="supervisors"
                :loading="dictionariesLoading"
                :rules="[rules.selectionRequired]"
                item-value="id"
                item-text="name"
                label="РГ (опционально)"
                no-data-text="Ничего не найдено"
                multiple
                chips
                small-chips
                deletable-chips
                clearable
              />
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <v-menu
                v-model="monthMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    :value="formattedMonth"
                    label="Месяц"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="filters.month"
                  type="month"
                  locale="Ru-ru"
                  :max="maxMonth"
                  @change="monthMenu = false"
                />
              </v-menu>
            </v-col>

            <v-col cols="12" sm="6" md="3" class="d-flex align-center justify-end flex-wrap">
              <v-btn
                color="success"
                class="mr-2 mb-1"
                :loading="loading"
                :disabled="loading || exporting"
                @click="loadReport"
              >
                Показать
              </v-btn>
              <v-btn
                color="primary"
                outlined
                class="mb-1"
                :loading="exporting"
                :disabled="loading || exporting"
                @click="exportReport"
              >
                Выгрузить
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <v-card v-if="report" class="mt-5">
      <v-card-text>
        <v-row dense class="mb-4">
          <v-col cols="12" md="6">
            <div><strong>Месяц:</strong> {{ report.month }}</div>
            <div>
              <strong>Период:</strong>
              {{ formatDateTime(report.period && report.period.from) }}
              —
              {{ formatDateTime(report.period && report.period.to) }}
            </div>
            <div>
              <strong>РГ:</strong>
              {{ reportRgNames || '—' }}
            </div>
            <div v-if="loading" class="mt-1 grey--text">
              Загружено {{ loadProgress.done }}/{{ loadProgress.total }}
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div>
              <strong>Всего проектов:</strong> {{ projectsTotal != null ? projectsTotal : '—' }}
              <v-btn
                v-if="reportProjects.length"
                icon
                x-small
                type="button"
                @click="projectsDialog = true"
              >
                <v-icon small>mdi-help-circle-outline</v-icon>
              </v-btn>
            </div>
            <div><strong>База премии:</strong> {{ formatMoney(report.bonus_base) }} ₽</div>
            <div>
              <strong>Формула ИЭ:</strong>
              претензии×{{ weightLabel(report.weights, 'complaints') }}
              + сервис×{{ weightLabel(report.weights, 'service') }}
              + прослушка×{{ weightLabel(report.weights, 'wiretapping') }}
              + занятость×{{ weightLabel(report.weights, 'employment') }}
            </div>
            <div><strong>Премия</strong> = база × ИЭ</div>
          </v-col>
        </v-row>

        <v-alert
          v-if="!loading && !(report.rgs && report.rgs.length)"
          type="info"
          text
          dense
        >
          Нет данных по выбранным РГ
        </v-alert>

        <div v-if="loading || (report.rgs && report.rgs.length)" class="table-wrap">
          <v-data-table
            :headers="headers"
            :items="tableRows"
            :loading="loading && !tableRows.length"
            item-key="row_key"
            dense
            hide-default-footer
            :items-per-page="tableRows.length || 1"
            no-data-text="Загрузка…"
            class="elevation-0 kpi-table"
            :item-class="rowClass"
          >
            <template #item.rg="{ item }">
              <div class="d-flex align-center justify-start">
                <div>
                  <div>{{ item.rg && item.rg.name }}</div>
                  <div v-if="item.rg && item.rg.sip" class="caption grey--text">
                    СИП {{ item.rg.sip }}
                  </div>
                </div>
                <v-tooltip v-if="item.is_overridden" bottom>
                  <template #activator="{ on, attrs }">
                    <v-icon
                      small
                      color="warning"
                      class="ml-1"
                      v-bind="attrs"
                      v-on="on"
                    >
                      mdi-pencil
                    </v-icon>
                  </template>
                  <span>Есть ручные правки</span>
                </v-tooltip>
              </div>
            </template>

            <template #item.complaints="{ item }">
              <div class="editable-cell" @click="openEdit(item, 'complaints')">
                <div>{{ formatIndex(item.complaints && item.complaints.index) }}</div>
                <div class="caption grey--text">
                  {{ item.complaints && item.complaints.projects_without_claims }}/{{ item.complaints && item.complaints.projects_total }}
                </div>
              </div>
            </template>

            <template #item.service="{ item }">
              <div class="editable-cell" @click="openEdit(item, 'service')">
                <div>{{ formatIndex(item.service && item.service.index) }}</div>
                <div class="caption grey--text">
                  {{ item.service && item.service.projects_in_norm }}/{{ item.service && item.service.projects_total }}
                </div>
              </div>
            </template>

            <template #item.wiretapping="{ item }">
              <div class="editable-cell" @click="openEdit(item, 'wiretapping')">
                <div>{{ formatIndex(item.wiretapping && item.wiretapping.index) }}</div>
                <div class="caption grey--text">
                  {{ item.wiretapping && item.wiretapping.projects_done }}/{{ item.wiretapping && item.wiretapping.projects_total }}
                </div>
              </div>
            </template>

            <template #item.employment="{ item }">
              <div class="editable-cell" @click="openEdit(item, 'employment')">
                <div>{{ formatCoefficient(item.employment && item.employment.index) }}</div>
                <div class="caption grey--text">
                  просадки {{ item.employment && item.employment.failed_days }} дн.
                </div>
              </div>
            </template>

            <template #item.efficiency_index="{ item }">
              <div class="editable-cell" @click="openEdit(item, 'efficiency_index')">
                {{ formatCoefficient(item.efficiency_index) }}
              </div>
            </template>

            <template #item.bonus="{ item }">
              <div class="editable-cell" @click="openEdit(item, 'bonus')">
                {{ formatMoney(item.bonus) }} ₽
              </div>
            </template>

            <template #item.actions="{ item }">
              <div class="d-flex align-center justify-center flex-nowrap">
                <v-btn x-small color="info" text type="button" @click="openDetails(item)">
                  Детали
                </v-btn>
                <v-btn
                  v-if="item.is_overridden"
                  x-small
                  color="warning"
                  text
                  type="button"
                  :loading="!!resetting[item.row_key]"
                  :disabled="!!saving[item.row_key] || !!resetting[item.row_key]"
                  @click="resetOverride(item)"
                >
                  Сбросить
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>

    <v-dialog v-model="projectsDialog" max-width="560" scrollable>
      <v-card v-if="projectsDialog">
        <v-card-title>
          Проекты ({{ reportProjects.length }})
          <v-spacer />
          <v-btn icon @click="projectsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text style="max-height: 70vh;">
          <v-simple-table dense>
            <tbody>
              <tr v-for="(project, idx) in reportProjects" :key="'pr-' + project.id">
                <td class="grey--text" style="width: 40px;">{{ idx + 1 }}</td>
                <td>{{ project.name }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editDialog" max-width="420" persistent>
      <v-card v-if="editDialog">
        <v-card-title>
          {{ editTitle }}
          <v-spacer />
          <v-btn icon :disabled="editSaving" @click="editDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="mb-3 grey--text">
            {{ editRow && editRow.rg && editRow.rg.name }} · {{ filters.month }}
          </div>

          <template v-if="editField === 'complaints'">
            <v-text-field
              v-model.number="editForm.indexPercent"
              label="Индекс, %"
              type="number"
              step="0.1"
              hide-details="auto"
              class="mb-2"
            />
            <v-text-field
              v-model.number="editForm.projects_without_claims"
              label="Без претензий"
              type="number"
              hide-details="auto"
              class="mb-2"
            />
            <v-text-field
              v-model.number="editForm.projects_total"
              label="Всего проектов"
              type="number"
              hide-details="auto"
            />
          </template>

          <template v-else-if="editField === 'service'">
            <v-text-field
              v-model.number="editForm.indexPercent"
              label="Индекс, %"
              type="number"
              step="0.1"
              hide-details="auto"
              class="mb-2"
            />
            <v-text-field
              v-model.number="editForm.projects_in_norm"
              label="В норме"
              type="number"
              hide-details="auto"
              class="mb-2"
            />
            <v-text-field
              v-model.number="editForm.projects_total"
              label="Всего проектов"
              type="number"
              hide-details="auto"
            />
          </template>

          <template v-else-if="editField === 'wiretapping'">
            <v-text-field
              v-model.number="editForm.indexPercent"
              label="Индекс, %"
              type="number"
              step="0.1"
              hide-details="auto"
              class="mb-2"
            />
            <v-text-field
              v-model.number="editForm.projects_done"
              label="Прослушано"
              type="number"
              hide-details="auto"
              class="mb-2"
            />
            <v-text-field
              v-model.number="editForm.projects_total"
              label="Всего проектов"
              type="number"
              hide-details="auto"
            />
          </template>

          <template v-else-if="editField === 'employment'">
            <v-text-field
              v-model.number="editForm.index"
              label="Коэффициент"
              type="number"
              step="0.01"
              hide-details="auto"
              class="mb-2"
            />
            <v-text-field
              v-model.number="editForm.failed_days"
              label="Просадки, дн."
              type="number"
              hide-details="auto"
            />
          </template>

          <template v-else-if="editField === 'efficiency_index'">
            <v-text-field
              v-model.number="editForm.efficiency_index"
              label="ИЭ"
              type="number"
              step="0.01"
              hide-details="auto"
            />
          </template>

          <template v-else-if="editField === 'bonus'">
            <v-text-field
              v-model.number="editForm.bonus"
              label="Премия, ₽"
              type="number"
              hide-details="auto"
            />
          </template>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="editSaving" @click="editDialog = false">Отмена</v-btn>
          <v-btn color="primary" :loading="editSaving" @click="saveEdit">Сохранить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="detailsDialog" max-width="960" scrollable>
      <v-card v-if="detailsDialog">
        <v-card-title>
          Детали: {{ detailsTitle }}
          <v-spacer />
          <v-btn icon @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text style="max-height: 70vh;">
          <v-expansion-panels multiple>
            <v-expansion-panel>
              <v-expansion-panel-header>
                Прослушка
                ({{ wiretappingSummary }})
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>Проект</th>
                      <th class="text-right">Звонки</th>
                      <th class="text-right">Прослушано</th>
                      <th class="text-right">%</th>
                      <th class="text-right">Цель РГ</th>
                      <th class="text-center">Done</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in wiretappingRows"
                      :key="'wt-' + row.row_key"
                    >
                      <td>{{ row.project_name }}</td>
                      <td class="text-right">{{ row.calls }}</td>
                      <td class="text-right">{{ row.listened }}</td>
                      <td class="text-right">{{ formatPercent(row.rate) }}</td>
                      <td class="text-right">{{ formatPercent(row.target_rg) }}</td>
                      <td class="text-center">
                        <v-icon small :color="row.done ? 'success' : 'error'">
                          {{ row.done ? 'mdi-check' : 'mdi-close' }}
                        </v-icon>
                      </td>
                    </tr>
                    <tr v-if="!wiretappingRows.length">
                      <td colspan="6" class="text-center grey--text">Нет данных</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header>
                Претензии
                (проектов с претензиями: {{ claimProjectNames.length }})
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="claimProjectNames.length">
                  {{ claimProjectNames.join(', ') }}
                </div>
                <div v-else class="grey--text">Нет проектов с претензиями</div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header>
                Сервис
                (ниже нормы принятых: {{ belowNormProjectNames.length }})
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="belowNormProjectNames.length">
                  Ниже нормы принятых: {{ belowNormProjectNames.join(', ') }}
                </div>
                <div v-else class="grey--text">Все проекты в норме</div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header>
                Занятость
                ({{ employmentSummary }})
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-if="employmentRows.length">
                  <div
                    v-for="row in employmentRows"
                    :key="row.row_key"
                    class="mb-2"
                  >
                    {{ row.fio }} — {{ row.dates_text }}
                  </div>
                </div>
                <div v-else class="grey--text">Нет просадок</div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'

const EDIT_TITLES = {
  complaints: 'Инд. претензий',
  service: 'Инд. сервис',
  wiretapping: 'Инд. прослушка',
  employment: 'Занятость',
  efficiency_index: 'ИЭ',
  bonus: 'Премия',
}

export default {
  name: 'KpiRg',

  data() {
    return {
      loading: false,
      exporting: false,
      dictionariesLoading: false,
      monthMenu: false,
      groups: [],
      supervisors: [],
      filters: {
        groupIds: [],
        rgIds: [],
        month: moment().subtract(1, 'month').format('YYYY-MM'),
      },
      report: null,
      lastFilterPayload: null,
      loadId: 0,
      loadProgress: { done: 0, total: 0 },
      detailsDialog: false,
      projectsDialog: false,
      selectedRg: null,
      editDialog: false,
      editSaving: false,
      editRow: null,
      editField: null,
      editForm: {},
      saving: {},
      resetting: {},
      headers: [
        { text: 'РГ', value: 'rg', sortable: false },
        { text: 'Инд. претензий', value: 'complaints', sortable: false, align: 'right' },
        { text: 'Инд. сервис', value: 'service', sortable: false, align: 'right' },
        { text: 'Инд. прослушка', value: 'wiretapping', sortable: false, align: 'right' },
        { text: 'Занятость', value: 'employment', sortable: false, align: 'right' },
        { text: 'ИЭ', value: 'efficiency_index', sortable: false, align: 'right' },
        { text: 'Премия', value: 'bonus', sortable: false, align: 'right' },
        { text: '', value: 'actions', sortable: false, align: 'center', width: '160px' },
      ],
      rules: {
        selectionRequired: () => {
          const hasGroups = Array.isArray(this.filters.groupIds) && this.filters.groupIds.length > 0
          const hasRgs = Array.isArray(this.filters.rgIds) && this.filters.rgIds.length > 0
          return hasGroups || hasRgs || 'Выберите группу или РГ'
        },
      },
    }
  },

  computed: {
    formattedMonth() {
      return this.filters.month
        ? moment(this.filters.month, 'YYYY-MM').locale('ru').format('MMMM YYYY')
        : ''
    },

    maxMonth() {
      return moment().subtract(1, 'month').format('YYYY-MM')
    },

    editTitle() {
      return EDIT_TITLES[this.editField] || 'Редактирование'
    },

    reportRgNames() {
      if (!this.report) return ''

      const selectedGroups = this.groups
        .filter((item) => this.filters.groupIds.includes(item.id))
        .map((item) => item.name)
      const selectedRgs = this.supervisors
        .filter((item) => this.filters.rgIds.includes(item.id))
        .map((item) => item.name)

      const parts = []
      if (selectedGroups.length) parts.push(`группы: ${selectedGroups.join(', ')}`)
      if (selectedRgs.length) parts.push(selectedRgs.join(', '))
      if (parts.length) return parts.join('; ')

      const fromRows = ((this.report.rgs) || [])
        .map((row) => row.rg && row.rg.name)
        .filter(Boolean)

      return fromRows.join(', ')
    },

    reportProjects() {
      const list = (this.report && this.report.projects) || []
      return list.slice().sort((a, b) => String(a.name).localeCompare(String(b.name), 'ru'))
    },

    projectsTotal() {
      if (!this.report) return null
      if (Array.isArray(this.report.projects)) return this.report.projects.length
      return this.report.projects_total
    },

    tableRows() {
      const rgs = (this.report && this.report.rgs) || []
      return rgs.map((row, idx) => ({
        ...row,
        row_key: (row.rg && row.rg.id) || `rg-${idx}`,
      }))
    },

    detailsTitle() {
      return (this.selectedRg && this.selectedRg.rg && this.selectedRg.rg.name) || ''
    },

    wiretappingSummary() {
      const data = this.selectedRg && this.selectedRg.wiretapping
      if (!data) return '0/0'
      return `${data.projects_done || 0}/${data.projects_total || 0}`
    },

    wiretappingRows() {
      const raw = this.selectedRg && this.selectedRg.wiretapping && this.selectedRg.wiretapping.details
      const list = Array.isArray(raw) ? raw : []
      return list.map((row, idx) => ({
        row_key: (row && row.project_id) || idx,
        project_name: (row && row.project_name) || '',
        calls: row && row.calls,
        listened: row && row.listened,
        rate: row && row.rate,
        target_rg: row && row.target_rg,
        done: !!(row && row.done),
      }))
    },

    claimProjectNames() {
      const claims = this.selectedRg && this.selectedRg.complaints && this.selectedRg.complaints.projects_with_claims
      if (!Array.isArray(claims)) return []
      return claims.map((item) => (typeof item === 'string' ? item : (item && item.name) || '')).filter(Boolean)
    },

    belowNormProjectNames() {
      const projects = this.selectedRg && this.selectedRg.service && this.selectedRg.service.projects_below_norm
      if (!Array.isArray(projects)) return []
      return projects.map((item) => (typeof item === 'string' ? item : (item && item.name) || '')).filter(Boolean)
    },

    employmentSummary() {
      const data = this.selectedRg && this.selectedRg.employment
      if (!data) return 'просадки — дн.'
      return `просадки ${data.failed_days} дн.`
    },

    employmentRows() {
      const raw = this.selectedRg && this.selectedRg.employment && this.selectedRg.employment.details
      const list = Array.isArray(raw) ? raw : []

      return list.map((row, idx) => {
        if (typeof row === 'string') {
          return {
            row_key: `emp-${idx}`,
            fio: '',
            dates_text: row,
          }
        }

        const dates = (row && Array.isArray(row.dates)) ? row.dates.filter((d) => typeof d === 'string') : []
        const fio = (row && row.name && String(row.name).trim())
          || [row && (row.last_name || row.lastName), row && (row.first_name || row.firstName)].filter(Boolean).join(' ').trim()
          || '—'

        return {
          row_key: `emp-${(row && row.operator_id) || idx}`,
          fio,
          dates_text: dates.length
            ? dates.map((d) => this.formatEmploymentDate(d)).join(', ')
            : 'Нет просадок',
        }
      })
    },
  },

  mounted() {
    this.loadDictionaries()
  },

  methods: {
    ...mapActions({
      fetchGroups: 'kpi_rg/fetchGroups',
      fetchSupervisors: 'kpi_rg/fetchSupervisors',
      fetchReport: 'kpi_rg/fetchReport',
      exportReportRequest: 'kpi_rg/exportReport',
      saveOverride: 'kpi_rg/saveOverride',
      deleteOverride: 'kpi_rg/deleteOverride',
      setError: 'shared/setError',
      setSuccess: 'shared/setSuccess',
    }),

    rowClass(item) {
      return item && item.is_overridden ? 'row-overridden' : ''
    },

    async loadDictionaries() {
      this.dictionariesLoading = true
      try {
        const [groups, supervisors] = await Promise.all([
          this.fetchGroups(),
          this.fetchSupervisors(),
        ])
        this.supervisors = supervisors
        const supervisorIds = new Set(supervisors.map((item) => item.id))
        // Бэк резолвит group_ids → только РГ (supervisor). Группы без РГ дают 422.
        this.groups = groups.filter((group) => (
          (group.members_ids || []).some((id) => supervisorIds.has(id))
        ))
      } catch (error) {
        const payload = (error.response && error.response.data) || { message: error }
        this.setError(payload)
      } finally {
        this.dictionariesLoading = false
      }
    },

    normalizeIds(values) {
      return []
        .concat(values || [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)
    },

    buildFilterPayload() {
      const payload = {
        month: this.filters.month,
      }
      const groupIds = this.normalizeIds(this.filters.groupIds)
      const rgIds = this.normalizeIds(this.filters.rgIds)
      if (groupIds.length) payload.group_ids = groupIds
      if (rgIds.length) payload.rg_ids = rgIds
      if (this.report && this.report.bonus_base != null) {
        payload.bonus_base = this.report.bonus_base
      }
      return payload
    },

    reportErrorPayload(error) {
      const payload = (error.response && error.response.data) || { message: error }
      const rgErrors = payload.errors && payload.errors.rg_ids
      if (Array.isArray(rgErrors) && rgErrors.some((msg) => String(msg).includes('group_id'))) {
        return {
          ...payload,
          message: 'В выбранных группах нет РГ. Выберите другую группу или укажите РГ вручную.',
        }
      }
      return payload
    },

    applyChunk(chunk) {
      if (!chunk || !this.report) return

      if (chunk.month) this.$set(this.report, 'month', chunk.month)
      if (chunk.period) this.$set(this.report, 'period', chunk.period)
      if (chunk.weights) this.$set(this.report, 'weights', chunk.weights)
      if (chunk.bonus_base != null) this.$set(this.report, 'bonus_base', chunk.bonus_base)
      if (chunk.projects_total != null) this.$set(this.report, 'projects_total', chunk.projects_total)
      if (Array.isArray(chunk.projects)) {
        const known = new Set((this.report.projects || []).map((project) => project.id))
        const added = chunk.projects.filter((project) => !known.has(project.id))
        this.$set(this.report, 'projects', (this.report.projects || []).concat(added))
      }

      const rows = Array.isArray(chunk.rgs) ? chunk.rgs : []
      if (!rows.length) return

      const knownRg = new Set((this.report.rgs || []).map((row) => row.rg && Number(row.rg.id)))
      const unique = rows.filter((row) => {
        const id = row.rg && Number(row.rg.id)
        if (!id || knownRg.has(id)) return false
        knownRg.add(id)
        return true
      })
      this.report.rgs = (this.report.rgs || []).concat(unique)
    },

    async loadReport() {
      if (!this.$refs.form.validate()) return

      const filterPayload = this.buildFilterPayload()
      if (!filterPayload.group_ids && !filterPayload.rg_ids) return

      const month = filterPayload.month
      const loadId = this.loadId + 1
      this.loadId = loadId
      this.lastFilterPayload = { ...filterPayload }

      this.loading = true
      this.report = {
        month,
        period: null,
        projects_total: null,
        projects: null,
        bonus_base: null,
        weights: null,
        rgs: [],
      }

      // По группам — один запрос; только по списку РГ — параллельно по одному
      const onlyRgIds = !filterPayload.group_ids && Array.isArray(filterPayload.rg_ids)
        ? filterPayload.rg_ids
        : null

      try {
        if (onlyRgIds && onlyRgIds.length > 1) {
          this.loadProgress = { done: 0, total: onlyRgIds.length }
          await Promise.all(onlyRgIds.map((rgId) => this.fetchReport({
            rg_ids: [rgId],
            month,
            ...(filterPayload.bonus_base != null ? { bonus_base: filterPayload.bonus_base } : {}),
          }).then((chunk) => {
            if (loadId !== this.loadId) return
            this.applyChunk(chunk)
          }).catch((error) => {
            if (loadId !== this.loadId) return
            this.setError(this.reportErrorPayload(error))
          }).finally(() => {
            if (loadId === this.loadId) {
              this.loadProgress.done += 1
            }
          })))
        } else {
          this.loadProgress = { done: 0, total: 1 }
          const chunk = await this.fetchReport(filterPayload)
          if (loadId !== this.loadId) return
          this.applyChunk(chunk)
          this.loadProgress.done = 1
        }
      } catch (error) {
        if (loadId !== this.loadId) return
        this.report = null
        this.setError(this.reportErrorPayload(error))
      } finally {
        if (loadId === this.loadId) {
          this.loading = false
        }
      }
    },

    async exportReport() {
      if (!this.$refs.form.validate()) return

      const filterPayload = this.buildFilterPayload()
      if (!filterPayload.group_ids && !filterPayload.rg_ids) return

      this.exporting = true
      try {
        const resp = await this.exportReportRequest(filterPayload)
        this.downloadExportBlob(resp, filterPayload.month)
      } catch (error) {
        this.setError(this.reportErrorPayload(error))
      } finally {
        this.exporting = false
      }
    },

    downloadExportBlob(response, month) {
      try {
        if (this.utils && typeof this.utils.downloadXlsx === 'function') {
          this.utils.downloadXlsx(response)
          return
        }
      } catch (e) {
        // fallback ниже
      }

      const url = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.download = `kpi_rg_${month || 'export'}.xlsx`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },

    async reloadRgRow(rgId) {
      const chunk = await this.fetchReport({
        rg_ids: [Number(rgId)],
        month: this.filters.month,
        ...(this.report && this.report.bonus_base != null
          ? { bonus_base: this.report.bonus_base }
          : {}),
      })
      const rows = (chunk && Array.isArray(chunk.rgs)) ? chunk.rgs : []
      const next = rows[0]
      if (!next || !this.report) return

      const idx = (this.report.rgs || []).findIndex((row) => row.rg && Number(row.rg.id) === Number(rgId))
      if (idx >= 0) {
        this.$set(this.report.rgs, idx, next)
      } else {
        this.report.rgs = (this.report.rgs || []).concat([next])
      }

      if (this.selectedRg && this.selectedRg.rg && Number(this.selectedRg.rg.id) === Number(rgId)) {
        this.selectedRg = { ...next, row_key: next.rg && next.rg.id }
      }

      if (chunk.bonus_base != null) this.$set(this.report, 'bonus_base', chunk.bonus_base)
      if (chunk.weights) this.$set(this.report, 'weights', chunk.weights)
    },

    openDetails(item) {
      this.selectedRg = item
      this.detailsDialog = true
    },

    openEdit(item, field) {
      if (!item || !item.rg || !item.rg.id) return
      if (this.loading || this.saving[item.row_key] || this.resetting[item.row_key]) return

      this.editRow = item
      this.editField = field
      this.editForm = this.buildEditForm(item, field)
      this.editDialog = true
    },

    buildEditForm(item, field) {
      if (field === 'complaints') {
        const data = item.complaints || {}
        return {
          indexPercent: this.toPercentInput(data.index),
          projects_without_claims: data.projects_without_claims,
          projects_total: data.projects_total,
        }
      }
      if (field === 'service') {
        const data = item.service || {}
        return {
          indexPercent: this.toPercentInput(data.index),
          projects_in_norm: data.projects_in_norm,
          projects_total: data.projects_total,
        }
      }
      if (field === 'wiretapping') {
        const data = item.wiretapping || {}
        return {
          indexPercent: this.toPercentInput(data.index),
          projects_done: data.projects_done,
          projects_total: data.projects_total,
        }
      }
      if (field === 'employment') {
        const data = item.employment || {}
        return {
          index: data.index,
          failed_days: data.failed_days,
        }
      }
      if (field === 'efficiency_index') {
        return { efficiency_index: item.efficiency_index }
      }
      if (field === 'bonus') {
        return { bonus: item.bonus }
      }
      return {}
    },

    buildOverridePayload() {
      const rgId = this.editRow && this.editRow.rg && this.editRow.rg.id
      const payload = {
        rg_id: Number(rgId),
        month: this.filters.month,
      }

      if (this.editField === 'complaints') {
        payload.complaints = {
          index: this.fromPercentInput(this.editForm.indexPercent),
          projects_without_claims: Number(this.editForm.projects_without_claims),
          projects_total: Number(this.editForm.projects_total),
        }
      } else if (this.editField === 'service') {
        payload.service = {
          index: this.fromPercentInput(this.editForm.indexPercent),
          projects_in_norm: Number(this.editForm.projects_in_norm),
          projects_total: Number(this.editForm.projects_total),
        }
      } else if (this.editField === 'wiretapping') {
        payload.wiretapping = {
          index: this.fromPercentInput(this.editForm.indexPercent),
          projects_done: Number(this.editForm.projects_done),
          projects_total: Number(this.editForm.projects_total),
        }
      } else if (this.editField === 'employment') {
        payload.employment = {
          index: Number(this.editForm.index),
          failed_days: Number(this.editForm.failed_days),
        }
      } else if (this.editField === 'efficiency_index') {
        payload.efficiency_index = Number(this.editForm.efficiency_index)
      } else if (this.editField === 'bonus') {
        payload.bonus = Number(this.editForm.bonus)
      }

      return payload
    },

    async saveEdit() {
      if (!this.editRow || !this.editField) return

      const rgId = this.editRow.rg && this.editRow.rg.id
      const rowKey = this.editRow.row_key
      const payload = this.buildOverridePayload()

      this.editSaving = true
      this.$set(this.saving, rowKey, true)
      try {
        await this.saveOverride(payload)
        this.editDialog = false
        this.setSuccess({ message: 'Правка сохранена' })
        await this.reloadRgRow(rgId)
      } catch (error) {
        const err = (error.response && error.response.data) || { message: error }
        this.setError(err)
      } finally {
        this.editSaving = false
        this.$delete(this.saving, rowKey)
      }
    },

    async resetOverride(item) {
      const rgId = item && item.rg && item.rg.id
      if (!rgId) return

      const rowKey = item.row_key
      this.$set(this.resetting, rowKey, true)
      try {
        await this.deleteOverride({
          rg_id: Number(rgId),
          month: this.filters.month,
        })
        this.setSuccess({ message: 'Правки сброшены' })
        await this.reloadRgRow(rgId)
      } catch (error) {
        const err = (error.response && error.response.data) || { message: error }
        this.setError(err)
      } finally {
        this.$delete(this.resetting, rowKey)
      }
    },

    toPercentInput(value) {
      if (value == null || value === '') return null
      return Math.round(Number(value) * 1000) / 10
    },

    fromPercentInput(value) {
      return Math.round(Number(value) * 10) / 1000
    },

    formatIndex(value) {
      if (value == null || value === '') return '—'
      return `${Math.round(Number(value) * 1000) / 10}%`
    },

    formatCoefficient(value) {
      if (value == null || value === '') return '—'
      return value
    },

    formatPercent(value) {
      if (value == null || value === '') return '—'
      return `${Math.round(Number(value) * 1000) / 10}%`
    },

    formatMoney(value) {
      if (value == null || value === '') return '—'
      return Number(value).toLocaleString('ru-RU')
    },

    formatDateTime(value) {
      if (!value) return '—'
      return moment(value).format('DD.MM.YYYY HH:mm')
    },

    formatEmploymentDate(value) {
      if (!value) return ''
      const parsed = moment(value, ['YYYY-MM-DD', 'DD.MM.YYYY', moment.ISO_8601], true)
      return parsed.isValid() ? parsed.format('DD.MM') : String(value)
    },

    weightLabel(weights, key) {
      if (!weights || weights[key] == null) return '—'
      return weights[key]
    },
  },
}
</script>

<style scoped>
.table-wrap {
  overflow-x: auto;
}

.editable-cell {
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
}

.editable-cell:hover {
  background: rgba(25, 118, 210, 0.08);
}

.kpi-table >>> .row-overridden {
  background: rgba(255, 193, 7, 0.08);
}
</style>
